import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  Modal,
  inputClass,
} from '../../components/ui';
import { apiOrigin, errorMessage } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { PERMISSION_GROUPS, permissionLabel } from '../../lib/permissions';
import { apiKeysApi } from '../../lib/resources';
import type { ApiKey, PermissionCode } from '../../types';

const MCP_URL = `${apiOrigin()}/mcp`;

const STATUS: Record<ApiKey['status'], [string, string]> = {
  active: ['Ativa', 'bg-emerald-50 text-emerald-700'],
  expired: ['Vencida', 'bg-amber-50 text-amber-700'],
  revoked: ['Revogada', 'bg-slate-100 text-slate-500'],
};

/** Configurações › Chaves de API e MCP (Fase 5 / P1) — só administrador. */
export function ApiKeysSection() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<{ name: string; key: string } | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const list = useQuery({ queryKey: ['api-keys'], queryFn: apiKeysApi.list });
  const revoke = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: () => {
      setRevoking(null);
      void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-slate-200 bg-white">
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Chaves de API</h2>
            <p className="text-xs text-slate-500">
              Para integrações e para o MCP. A chave age com o papel escolhido, neste workspace.
            </p>
          </div>
          <Button variant="primary" onClick={() => setCreating(true)}>
            <span className="whitespace-nowrap">+ Nova chave</span>
          </Button>
        </header>
        {list.isLoading ? (
          <Loading />
        ) : list.isError ? (
          <div className="p-5">
            <ErrorNote>{errorMessage(list.error)}</ErrorNote>
          </div>
        ) : !list.data?.length ? (
          <div className="p-5">
            <EmptyState
              title="Nenhuma chave"
              hint="Crie uma para conectar outro sistema ou um assistente via MCP."
            />
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {list.data.map((key) => (
              <li
                key={key.id}
                className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm"
              >
                <div className="min-w-0">
                  <p className="font-medium text-slate-800">
                    {key.name}{' '}
                    <span
                      className={`ml-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS[key.status][1]}`}
                    >
                      {STATUS[key.status][0]}
                    </span>
                  </p>
                  <p className="text-xs text-slate-500">
                    <code>{key.prefix}…</code> ·{' '}
                    {key.isAdmin
                      ? 'Administrador'
                      : key.permissions.map(permissionLabel).join(', ')}
                  </p>
                  <p className="text-[11px] text-slate-400">
                    Criada {formatDateTime(key.createdAt)}
                    {key.createdByName && ` por ${key.createdByName}`}
                    {key.expiresAt && ` · vence ${formatDateTime(key.expiresAt)}`}
                    {key.lastUsedAt
                      ? ` · último uso ${formatDateTime(key.lastUsedAt)}`
                      : ' · nunca usada'}
                  </p>
                </div>
                {key.status !== 'revoked' && (
                  <Button variant="danger" onClick={() => setRevoking(key)}>
                    Revogar
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm text-slate-600">
        <h2 className="text-base font-semibold text-slate-900">MCP</h2>
        <p className="mt-1">
          Conecte um assistente (Claude, por exemplo) ao CRM pelo protocolo MCP, transporte
          Streamable HTTP:
        </p>
        <code className="mt-2 block break-all rounded bg-slate-50 p-2 text-xs text-slate-700">
          {MCP_URL}
        </code>
        <p className="mt-2 text-xs text-slate-500">
          Cabeçalho <code>Authorization: Bearer &lt;sua chave&gt;</code>. Ferramentas: leads,
          negócios, pipelines, atividades e conversas (só leitura) — sempre com o papel da
          chave.
        </p>
      </section>

      {creating && (
        <NewKeyModal
          onClose={() => setCreating(false)}
          onCreated={(name, key) => {
            setCreating(false);
            setRevealed({ name, key });
            void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
          }}
        />
      )}
      {revealed && <RevealModal {...revealed} onClose={() => setRevealed(null)} />}
      {revoking && (
        <ConfirmDialog
          title="Revogar chave"
          message={
            <>
              <strong>{revoking.name}</strong> para de funcionar na hora. Quem usa precisa de
              uma chave nova.
            </>
          }
          confirmLabel="Revogar"
          danger
          busy={revoke.isPending}
          error={revoke.isError ? errorMessage(revoke.error) : null}
          onConfirm={() => revoke.mutate(revoking.id)}
          onClose={() => setRevoking(null)}
        />
      )}
    </div>
  );
}

function NewKeyModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (name: string, key: string) => void;
}) {
  const [name, setName] = useState('');
  const [isAdmin, setIsAdmin] = useState(true);
  const [permissions, setPermissions] = useState<PermissionCode[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const create = useMutation({
    mutationFn: () =>
      apiKeysApi.create({
        name: name.trim(),
        isAdmin,
        permissions: isAdmin ? [] : permissions,
        expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : undefined,
      }),
    onSuccess: (created) => onCreated(created.name, created.key),
  });
  const toggle = (code: PermissionCode) =>
    setPermissions((current) =>
      current.includes(code) ? current.filter((item) => item !== code) : [...current, code],
    );

  return (
    <Modal title="Nova chave de API" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <Field label="Nome" hint="Para reconhecer depois (ex.: Site, Zapier, Claude)">
          <input
            autoFocus
            className={inputClass}
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <div className="space-y-2 text-sm">
          <p className="font-medium text-slate-700">Papel</p>
          <label className="flex items-center gap-2">
            <input type="radio" checked={isAdmin} onChange={() => setIsAdmin(true)} />
            Administrador (pode tudo, menos administrar chaves)
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" checked={!isAdmin} onChange={() => setIsAdmin(false)} />
            Papéis escolhidos
          </label>
          {!isAdmin && (
            <div className="max-h-56 space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-3">
              {PERMISSION_GROUPS.map((group) => (
                <div key={group.group}>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {group.group}
                  </p>
                  {group.items.map((item) => (
                    <label key={item.code} className="flex items-start gap-2 py-0.5 text-xs">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={permissions.includes(item.code)}
                        onChange={() => toggle(item.code)}
                      />
                      <span>
                        <span className="font-medium text-slate-700">{item.label}</span>
                        <span className="block text-slate-400">{item.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        <Field label="Validade (opcional)" hint="Sem data, a chave vale até ser revogada">
          <input
            type="date"
            className={inputClass}
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
        </Field>
        {create.isError && <ErrorNote>{errorMessage(create.error)}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!name.trim() || (!isAdmin && !permissions.length) || create.isPending}
          >
            {create.isPending ? 'Criando…' : 'Criar chave'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function RevealModal({
  name,
  key: value,
  onClose,
}: {
  name: string;
  key: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <Modal title={`Chave "${name}" criada`} onClose={onClose}>
      <div className="space-y-3 text-sm text-slate-600">
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
          Copie agora: este valor <strong>não aparece de novo</strong>. Se perder, revogue e
          crie outra.
        </p>
        <code className="block break-all rounded bg-slate-900 p-3 text-xs text-slate-100">
          {value}
        </code>
        <div className="flex justify-end gap-2">
          <Button
            onClick={() => {
              void navigator.clipboard.writeText(value);
              setCopied(true);
            }}
          >
            {copied ? 'Copiada' : 'Copiar'}
          </Button>
          <Button variant="primary" onClick={onClose}>
            Já copiei
          </Button>
        </div>
      </div>
    </Modal>
  );
}
